import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/changeInvoiceStatus.mutation"

import { type MutationLockResult, MutationLockResultStatus } from "@/entityLock/types"
import { toEntityLock } from "@/entityLock/utils"
import type {
  ChangeInvoiceStatusMutation,
  ChangeInvoiceStatusMutationVariables,
  InvoiceStatusChangeFailure,
  InvoiceStatusChangeInput,
} from "@/graphql/codegen/graphql"
import { ChangeInvoiceStatusDocument, InvoiceStatus } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export type InvoiceReviewAction = InvoiceStatus.Approved | InvoiceStatus.NotApproved | InvoiceStatus.Cancelled

export interface ReviewInvoiceInput extends Omit<InvoiceStatusChangeInput, "action"> {
  action: InvoiceReviewAction
}

export interface UseReviewInvoiceReturn {
  reviewInvoice: (input: ReviewInvoiceInput) => Promise<MutationLockResult>
  isReviewing: boolean
}

const REVIEW_INVOICE_ACTION_TO_SUCCESS_MESSAGE: Record<InvoiceReviewAction, string> = {
  [InvoiceStatus.Approved]: "Invoice approved",
  [InvoiceStatus.NotApproved]: "Invoice denied",
  [InvoiceStatus.Cancelled]: "Invoice cancelled",
}

export const useReviewInvoice = (): UseReviewInvoiceReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    ChangeInvoiceStatusMutation,
    ChangeInvoiceStatusMutationVariables
  >(ChangeInvoiceStatusDocument)

  const reviewInvoice = useCallback(
    async (input: ReviewInvoiceInput): Promise<MutationLockResult> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.invoice.statusChange

      if (output?.__typename === "EntityLockFailure") {
        const currentLock = toEntityLock(output.error.currentLock)!
        return {
          status: MutationLockResultStatus.LockConflict,
          currentLock,
          message: output.error.message,
        }
      }

      const hasError = error || output?.__typename === "InvoiceStatusChangeFailure"

      if (hasError) {
        const message = error?.message ?? (output as InvoiceStatusChangeFailure).error.message
        notify({ type: NotificationType.Error, message })
        return { status: MutationLockResultStatus.Error }
      }

      notify({
        type: NotificationType.Success,
        message: REVIEW_INVOICE_ACTION_TO_SUCCESS_MESSAGE[input.action],
      })
      return { status: MutationLockResultStatus.Success }
    },
    [executeMutation, notify]
  )

  return { reviewInvoice, isReviewing: fetching }
}
